import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  Box, 
  LinearProgress, 
  Typography, 
  IconButton,
  Toolbar,
  useMediaQuery
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import useWheelZoom from './useWheelZoom';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ImageIcon from '@mui/icons-material/Image';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import {
  VIEWER_TAG_HOVER_EVENT,
  doesTagHoverDetailMatch,
  getTagHighlightBackgroundColor,
  getTagHighlightColor,
  padNormalizedBbox,
} from './TagHighlightUtils';
import { useViewerFile } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';
const IMAGE_ROTATION_STEP_DEGREES = 90;

const normalizeCoordinateItems = (coordinates) => {
  if (!coordinates) {
    return [];
  }

  let parsedCoordinates = coordinates;
  if (typeof coordinates === 'string') {
    try {
      parsedCoordinates = JSON.parse(coordinates);
    } catch (error) {
      return [];
    }
  }

  return Array.isArray(parsedCoordinates) ? parsedCoordinates : [parsedCoordinates];
};

const normalizeTagHighlightBoxes = (timeevent) => {
  const fields = Array.isArray(timeevent?.customFields) ? timeevent.customFields : [];
  const boxes = [];

  fields.forEach((field) => {
    const coordinateItems = normalizeCoordinateItems(field?.coordinates);

    coordinateItems.forEach((coordinates) => {
      const bbox = coordinates?.bbox_percent;
      const page = Number(coordinates?.page || 1);
      if (!Array.isArray(bbox) || bbox.length !== 4 || !Number.isFinite(page) || page < 1) {
        return;
      }

      const normalizedBbox = bbox.map((value) => Number(value));
      if (normalizedBbox.some((value) => !Number.isFinite(value))) {
        return;
      }

      boxes.push({
        page,
        bbox: padNormalizedBbox(normalizedBbox) || normalizedBbox,
        label: field?.variable || '',
        field,
      });
    });
  });

  return boxes;
};

const normalizeRotation = (rotationDegrees) => (
  ((Number(rotationDegrees) || 0) % 360 + 360) % 360
);

const getRotatedImageSize = (imageSize, rotationDegrees) => {
  const rotation = normalizeRotation(rotationDegrees);
  const width = Number(imageSize?.width) || 0;
  const height = Number(imageSize?.height) || 0;

  if (rotation === 90 || rotation === 270) {
    return { width: height, height: width };
  }
  return { width, height };
};

const getImageRotationTransform = (rotationDegrees, imageSize) => {
  const rotation = normalizeRotation(rotationDegrees);
  const width = Number(imageSize?.width) || 0;
  const height = Number(imageSize?.height) || 0;

  if (!width || !height || rotation === 0) {
    return 'none';
  }
  if (rotation === 90) {
    return `translateX(${height}px) rotate(90deg)`;
  }
  if (rotation === 180) {
    return `translate(${width}px, ${height}px) rotate(180deg)`;
  }
  if (rotation === 270) {
    return `translateY(${width}px) rotate(270deg)`;
  }
  return 'none';
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start', // Changed from 'center' to 'flex-start' to show from top
  backgroundColor: '#f0f0f0',
  overflow: 'auto', // Restored scrollbars for better navigation when zoomed
  position: 'relative',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(87, 87, 176, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(87, 87, 176, 0.4)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.6)',
    },
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
}));

const StyledImage = styled('img')(() => ({
  width: '100%',
  height: '100%',
  display: 'block',
  borderRadius: '4px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  userSelect: 'none',
  pointerEvents: 'none',
}));



const ImageViewer = React.forwardRef(({ open, onClose, filename = '', eventId = null, filePath = '', timeevent = null, onTagHover = null, onTagHoverEnd = null, embedded = false }, _ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    fileBlob: imageBlob,
    fileUrl: imageUrl,
    loading,
    progress,
    error,
  } = useViewerFile({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.image_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageRotation, setImageRotation] = useState(0);
  const [hoveredTagDetail, setHoveredTagDetail] = useState(null);
  
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const lastScrolledHoverKeyRef = useRef(null);
  const tagHighlightBoxes = React.useMemo(() => normalizeTagHighlightBoxes(timeevent), [timeevent]);
  const renderedImageSize = getRotatedImageSize(imageSize, imageRotation);
  const imageRotationTransform = getImageRotationTransform(imageRotation, imageSize);
  const tagLabelTransform = imageRotation ? `rotate(${-normalizeRotation(imageRotation)}deg)` : 'none';
  const hasHoveredTagMatch = React.useMemo(() => (
    Boolean(hoveredTagDetail) &&
    tagHighlightBoxes.some((box) => (
      box.page === 1 &&
      doesTagHoverDetailMatch(hoveredTagDetail, box.field || { variable: box.label }, timeevent)
    ))
  ), [hoveredTagDetail, tagHighlightBoxes, timeevent]);

  useEffect(() => {
    if (typeof window === 'undefined' || !open) {
      return undefined;
    }

    const handleTagHover = (event) => {
      const detail = event?.detail || null;
      if (!detail) {
        lastScrolledHoverKeyRef.current = null;
      }
      setHoveredTagDetail(detail);
    };

    window.addEventListener(VIEWER_TAG_HOVER_EVENT, handleTagHover);
    return () => {
      window.removeEventListener(VIEWER_TAG_HOVER_EVENT, handleTagHover);
    };
  }, [open]);

  useEffect(() => {
    if (!hoveredTagDetail || !containerRef.current) {
      return;
    }

    const matchingHighlights = Array.from(
      containerRef.current.querySelectorAll('[data-tag-highlight="true"]')
    ).filter((element) => doesTagHoverDetailMatch(hoveredTagDetail, {
      id: element.dataset.tagId || null,
      timeeventid: element.dataset.eventId || null,
      variable: element.dataset.tagVariable || '',
    }, timeevent));

    if (!matchingHighlights.length) {
      return;
    }

    const requestedOccurrence = Number(hoveredTagDetail.occurrenceIndex);
    const target = Number.isInteger(requestedOccurrence) && requestedOccurrence >= 0
      ? matchingHighlights[requestedOccurrence] || matchingHighlights[0]
      : matchingHighlights[0];
    const hoverKey = [
      hoveredTagDetail.id || hoveredTagDetail.tagId || '',
      hoveredTagDetail.variable || '',
      Number.isInteger(requestedOccurrence) ? requestedOccurrence : 0,
    ].join(':');

    if (lastScrolledHoverKeyRef.current === hoverKey) {
      return;
    }
    lastScrolledHoverKeyRef.current = hoverKey;
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }, [hoveredTagDetail, tagHighlightBoxes, timeevent]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setZoom(1);
      setImageSize({ width: 0, height: 0 });
      setImageRotation(0);
      setHoveredTagDetail(null);
    } else {
      setZoom(1);
      setImageSize({ width: 0, height: 0 });
      setImageRotation(0);
      setHoveredTagDetail(null);
    }
  }, [open]);

  const downloadImage = () => {
    if (imageBlob) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const rotateImage = () => {
    setImageRotation((previousRotation) => (
      (previousRotation + IMAGE_ROTATION_STEP_DEGREES) % 360
    ));
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Zoom functions
  const zoomIn = () => {
    setZoom(prev => Math.min(prev * 1.5, 10));
  };

  const zoomOut = () => {
    setZoom(prev => Math.max(prev / 1.5, 0.1));
  };

  const resetZoom = () => {
    setZoom(1);
    // Reset scroll position
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  };

  useWheelZoom({
    containerRef,
    enabled: Boolean(imageUrl && !loading),
    maximumZoom: 10,
    minimumZoom: 0.1,
    setZoom,
    zoom,
  });

  const ViewerRoot = embedded ? Box : StyledDialog;
  const viewerRootProps = embedded
    ? { sx: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' } }
    : { open, onClose, maxWidth: false, fullScreen: isFullscreen || isSmallScreen };

  return (
    <ViewerRoot {...viewerRootProps}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <ImageIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.image_viewer_title')} - ${filename}` : t('viewer.image_viewer_title')}
          </Typography>
        </Box>
        {!embedded && <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>}
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadImage} 
            disabled={!imageBlob}
            size="small"
            title={t('viewer.download_image')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton
            onClick={rotateImage}
            disabled={!imageUrl}
            size="small"
            title={t('viewer.rotate_image', 'Rotate Image')}
          >
            <Rotate90DegreesCwIcon />
          </IconButton>
          <IconButton 
            onClick={zoomIn}
            disabled={!imageUrl}
            size="small"
            title="Zoom In"
          >
            <ZoomInIcon />
          </IconButton>
          <IconButton 
            onClick={zoomOut}
            disabled={!imageUrl}
            size="small"
            title="Zoom Out"
          >
            <ZoomOutIcon />
          </IconButton>
          <IconButton 
            onClick={resetZoom}
            disabled={!imageUrl}
            size="small"
            title="Reset Zoom"
          >
            <RestartAltIcon />
          </IconButton>
          <Typography variant="body2" sx={{ ml: 1 }}>
            {Math.round(zoom * 100)}%
          </Typography>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.loading_image')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <ImageContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 300, mb: 2 }}
              />
              <Typography color="text.secondary">
                {t('viewer.loading_image')} {Math.round(progress)}%
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_image')}
              </Typography>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Box>
          ) : imageUrl ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                minWidth: renderedImageSize.width ? `${renderedImageSize.width * zoom}px` : '100%',
                minHeight: renderedImageSize.height ? `${renderedImageSize.height * zoom}px` : '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: renderedImageSize.width ? `${renderedImageSize.width * zoom}px` : 'auto',
                  height: renderedImageSize.height ? `${renderedImageSize.height * zoom}px` : 'auto',
                  lineHeight: 0,
                  flexShrink: 0,
                }}
              >
                <Box
                  data-wheel-zoom-anchor="image"
                  sx={{
                    position: 'relative',
                    width: renderedImageSize.width ? `${renderedImageSize.width}px` : 'auto',
                    height: renderedImageSize.height ? `${renderedImageSize.height}px` : 'auto',
                    lineHeight: 0,
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: imageSize.width ? `${imageSize.width}px` : 'auto',
                      height: imageSize.height ? `${imageSize.height}px` : 'auto',
                      lineHeight: 0,
                      transform: imageRotationTransform,
                      transformOrigin: 'top left',
                    }}
                  >
                <StyledImage
                  ref={imageRef}
                  src={imageUrl}
                  alt={filename}
                  onLoad={(event) => {
                    const image = event.currentTarget;
                    setImageSize({
                      width: image.naturalWidth || image.width || 0,
                      height: image.naturalHeight || image.height || 0,
                    });
                    setZoom(1);
                    setImageRotation(0);
                    if (containerRef.current) {
                      containerRef.current.scrollLeft = 0;
                      containerRef.current.scrollTop = 0;
                    }
                  }}
                />
                {tagHighlightBoxes
                  .filter((box) => box.page === 1)
                  .map((box, index) => {
                    const [x0, y0, x1, y1] = box.bbox;
                    const tagColor = getTagHighlightColor(box.label);
                    const tagBackgroundColor = getTagHighlightBackgroundColor(box.label);
                    const isHoveredTag = doesTagHoverDetailMatch(
                      hoveredTagDetail,
                      box.field || { variable: box.label },
                      timeevent
                    );
                    const shouldHideTag = Boolean(hoveredTagDetail) && (!hasHoveredTagMatch || !isHoveredTag);
                    const left = Math.max(0, Math.min(1, x0)) * 100;
                    const top = Math.max(0, Math.min(1, y0)) * 100;
                    const width = (Math.max(0, Math.min(1, x1)) - Math.max(0, Math.min(1, x0))) * 100;
                    const height = (Math.max(0, Math.min(1, y1)) - Math.max(0, Math.min(1, y0))) * 100;

                    if (width <= 0 || height <= 0) {
                      return null;
                    }

                    return (
                      <Box
                        key={`${box.label}-${index}`}
                        data-tag-highlight="true"
                        data-tag-id={box.field?.id || ''}
                        data-event-id={box.field?.timeeventid || timeevent?.id || ''}
                        data-tag-variable={box.field?.variable || box.label || ''}
                        title={box.label}
                        onMouseEnter={() => {
                          if (onTagHover) {
                            onTagHover(box.field || { variable: box.label });
                          }
                        }}
                        onMouseLeave={() => {
                          if (onTagHoverEnd) {
                            onTagHoverEnd();
                          }
                        }}
                          sx={{
                            position: 'absolute',
                            left: `${left}%`,
                            top: `${top}%`,
                            width: `${width}%`,
                            height: `${height}%`,
                            border: `2px solid ${tagColor}`,
                            backgroundColor: tagBackgroundColor,
                            boxSizing: 'border-box',
                            pointerEvents: shouldHideTag ? 'none' : 'auto',
                            opacity: shouldHideTag ? 0 : (isHoveredTag ? 0.22 : 1),
                            boxShadow: isHoveredTag && !shouldHideTag
                              ? `0 0 0 3px ${tagColor}, 0 0 0 1px rgba(255,255,255,0.85) inset`
                              : 'none',
                            transition: 'opacity 120ms ease, box-shadow 120ms ease',
                            '&:hover': {
                              opacity: 0.22,
                              boxShadow: `0 0 0 3px ${tagColor}, 0 0 0 1px rgba(255,255,255,0.85) inset`,
                            },
                          }}
                        >
                        {box.label ? (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '-2px',
                              right: '-2px',
                              maxWidth: '160px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              backgroundColor: tagColor,
                              color: '#fff',
                              fontSize: '10px',
                              fontWeight: 700,
                              lineHeight: '14px',
                              padding: '1px 5px',
                              borderRadius: '0 0 0 3px',
                              boxSizing: 'border-box',
                              pointerEvents: 'none',
                              transform: tagLabelTransform,
                              transformOrigin: 'top right',
                            }}
                          >
                            {box.label}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">
              No image content available
            </Typography>
          )}
        </ImageContainer>
      </ViewerDialogContent>
    </ViewerRoot>
  );
});

// Set display name for debugging
ImageViewer.displayName = 'ImageViewer';

export default ImageViewer;
